/**
 * SiteBackground
 * A single full-page decorative layer that sits behind all content.
 * Contains:
 *  - Fine dot grid
 *  - Diagonal ruled lines (very subtle)
 *  - SVG circuit/flow paths with animated travelling dots
 *  - Large soft gradient orbs for colour depth
 *  - Ashoka-inspired 24-spoke wheel watermark (bottom-right)
 *  - Scattered micro-diamonds (Indian textile motif)
 *
 * Placed once in Landing.jsx via `fixed inset-0 -z-10`.
 */

import { motion } from "framer-motion";

/* ── Animated flow paths across the full page ────────────────── */
const FLOW_PATHS = [
  {
    d: "M -60 180 C 200 80, 480 320, 780 200 S 1200 60, 1560 180",
    dur: 14,
    delay: 0,
    r: 3.5,
  },
  {
    d: "M -60 420 C 280 340, 560 520, 860 430 S 1280 300, 1560 420",
    dur: 18,
    delay: 2,
    r: 2.8,
  },
  {
    d: "M -60 700 C 240 620, 520 800, 820 710 S 1240 580, 1560 700",
    dur: 22,
    delay: 4,
    r: 2.2,
  },
  {
    d: "M -60 960 C 300 880, 600 1040, 900 950 S 1300 820, 1560 960",
    dur: 16,
    delay: 1,
    r: 3,
  },
  {
    d: "M 300 -40 C 380 300, 260 600, 340 900 S 420 1200, 300 1560",
    dur: 20,
    delay: 3,
    r: 2.5,
  },
  {
    d: "M 900 -40 C 980 280, 860 580, 940 880 S 1020 1180, 900 1560",
    dur: 24,
    delay: 0.5,
    r: 2,
  },
];

/* ── Node positions for circuit dots ─────────────────────────── */
const NODES = [
  { cx: 180, cy: 200 },
  { cx: 480, cy: 380 },
  { cx: 760, cy: 170 },
  { cx: 1050, cy: 440 },
  { cx: 1280, cy: 220 },
  { cx: 320, cy: 680 },
  { cx: 680, cy: 820 },
  { cx: 1100, cy: 750 },
  { cx: 240, cy: 1050 },
  { cx: 820, cy: 1100 },
  { cx: 1380, cy: 980 },
];

/* ── Ashoka 24-spoke wheel as SVG ─────────────────────────────── */
function AshokaWheel({ size = 320 }) {
  const spokes = Array.from({ length: 24 }, (_, i) => i);
  const r = size / 2;
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="pointer-events-none"
    >
      {/* Outer ring */}
      <circle cx={r} cy={r} r={r - 4} fill="none" stroke="currentColor" strokeWidth="3" />
      <circle cx={r} cy={r} r={r - 14} fill="none" stroke="currentColor" strokeWidth="1" />
      {/* Hub */}
      <circle cx={r} cy={r} r={18} fill="none" stroke="currentColor" strokeWidth="2.5" />
      <circle cx={r} cy={r} r={8} fill="currentColor" opacity="0.5" />
      {/* Spokes */}
      {spokes.map((i) => {
        const angle = (i * 360) / 24 - 90;
        const rad = (angle * Math.PI) / 180;
        const x1 = r + 22 * Math.cos(rad);
        const y1 = r + 22 * Math.sin(rad);
        const x2 = r + (r - 16) * Math.cos(rad);
        const y2 = r + (r - 16) * Math.sin(rad);
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="currentColor"
            strokeWidth={i % 3 === 0 ? "1.5" : "0.8"}
          />
        );
      })}
      {/* Rim dots */}
      {spokes.map((i) => {
        const angle = (i * 360) / 24 - 90;
        const rad = (angle * Math.PI) / 180;
        const cx = r + (r - 8) * Math.cos(rad);
        const cy = r + (r - 8) * Math.sin(rad);
        return (
          <circle key={i} cx={cx} cy={cy} r={i % 3 === 0 ? 3.5 : 2} fill="currentColor" />
        );
      })}
    </svg>
  );
}

/* ── Micro-diamond scatter ────────────────────────────────────── */
const DIAMONDS = [
  { x: 60, y: 340, s: 6 },
  { x: 420, y: 140, s: 5 },
  { x: 730, y: 460, s: 7 },
  { x: 1100, y: 120, s: 5 },
  { x: 1340, y: 560, s: 6 },
  { x: 200, y: 820, s: 5 },
  { x: 580, y: 1000, s: 7 },
  { x: 980, y: 860, s: 5 },
  { x: 1420, y: 800, s: 6 },
  { x: 100, y: 1200, s: 5 },
  { x: 760, y: 1300, s: 6 },
  { x: 1260, cy: 1180, s: 5, y: 1180 },
];

export default function SiteBackground() {
  return (
    <div
      aria-hidden
      className="fixed inset-0 -z-10 overflow-hidden pointer-events-none select-none"
    >
      {/* ── Base gradient ──────────────────────────────────────── */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(160deg, hsl(42 38% 96%) 0%, hsl(38 32% 93%) 35%, hsl(40 28% 91%) 70%, hsl(42 35% 94%) 100%)",
        }}
      />

      {/* ── Fine dot grid — warm teal on cream ───────────────── */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle, hsl(32 60% 40% / 0.13) 1.4px, transparent 1.4px)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* ── Diagonal ruled lines — warm amber, not cool blue ──── */}
      <div
        className="absolute inset-0 opacity-[0.045]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, hsl(38 50% 35%) 0px, hsl(38 50% 35%) 1px, transparent 1px, transparent 52px)",
        }}
      />

      {/* ── Full-page SVG: flow paths, nodes, diamonds ────────── */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1560 1400"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Flow path routes */}
        {FLOW_PATHS.map((p, i) => (
          <g key={i}>
            {/* Route line */}
            <motion.path
              d={p.d}
              fill="none"
              stroke="hsl(38 50% 35%)"
              strokeWidth="0.8"
              strokeOpacity={0.14}
              strokeDasharray="5 18"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2.5, delay: i * 0.3, ease: "easeOut" }}
            />
            {/* Travelling dot — saffron */}
            <circle r={p.r} fill="hsl(32 98% 52%)" opacity="0.55">
              <animateMotion
                dur={`${p.dur}s`}
                repeatCount="indefinite"
                begin={`${p.delay}s`}
                path={p.d}
              />
              <animate
                attributeName="opacity"
                values="0;0.55;0.55;0"
                dur={`${p.dur}s`}
                repeatCount="indefinite"
                begin={`${p.delay}s`}
              />
            </circle>
            {/* Echo dot — sage */}
            <circle r={p.r * 0.65} fill="hsl(120 65% 35%)" opacity="0.35">
              <animateMotion
                dur={`${p.dur * 1.4}s`}
                repeatCount="indefinite"
                begin={`${p.delay + 2}s`}
                path={p.d}
              />
            </circle>
          </g>
        ))}

        {/* Circuit nodes */}
        {NODES.map((n, i) => (
          <g key={i}>
            <circle
              cx={n.cx}
              cy={n.cy}
              r={4}
              fill="none"
              stroke="hsl(38 50% 35%)"
              strokeWidth="1"
              opacity="0.20"
            />
            <circle cx={n.cx} cy={n.cy} r={1.8} fill="hsl(38 50% 35%)" opacity="0.25" />
            <line x1={n.cx - 8} y1={n.cy} x2={n.cx + 8} y2={n.cy} stroke="hsl(38 50% 35%)" strokeWidth="0.5" opacity="0.14" />
            <line x1={n.cx} y1={n.cy - 8} x2={n.cx} y2={n.cy + 8} stroke="hsl(38 50% 35%)" strokeWidth="0.5" opacity="0.14" />
          </g>
        ))}

        {/* Micro-diamonds */}
        {DIAMONDS.map((d, i) => (
          <rect
            key={i}
            x={d.x - d.s}
            y={d.y - d.s}
            width={d.s * 2}
            height={d.s * 2}
            fill="none"
            stroke="hsl(32 98% 52%)"
            strokeWidth="0.9"
            opacity="0.18"
            transform={`rotate(45 ${d.x} ${d.y})`}
          />
        ))}
      </svg>

      {/* ── Colour orbs — pulled back to not overpower cream ───── */}
      {/* Saffron — top right, pushed further off-screen, much weaker */}
      <div
        className="absolute -top-48 -right-48 w-[600px] h-[600px] rounded-full"
        style={{
          background: "radial-gradient(circle, hsl(32 98% 52% / 0.07) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />
      {/* Sage — bottom left */}
      <div
        className="absolute -bottom-48 -left-48 w-[560px] h-[560px] rounded-full"
        style={{
          background: "radial-gradient(circle, hsl(120 65% 35% / 0.06) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />
      {/* Warm amber — mid centre, very gentle depth */}
      <div
        className="absolute top-[40%] left-[35%] w-[480px] h-[380px] rounded-full"
        style={{
          background: "radial-gradient(ellipse, hsl(38 60% 55% / 0.05) 0%, transparent 70%)",
          filter: "blur(90px)",
        }}
      />
      {/* Teal — lower right, barely-there */}
      <div
        className="absolute bottom-[8%] right-[8%] w-[400px] h-[400px] rounded-full"
        style={{
          background: "radial-gradient(circle, hsl(209 52% 28% / 0.05) 0%, transparent 65%)",
          filter: "blur(80px)",
        }}
      />

      {/* ── Ashoka wheel watermark ─────────────────────────────── */}
      <div
        className="absolute bottom-[-40px] right-[-40px] text-[hsl(38_50%_35%)] opacity-[0.06]"
        style={{ transform: "rotate(-10deg)" }}
      >
        <AshokaWheel size={360} />
      </div>
      {/* Second smaller wheel — top-left */}
      <div
        className="absolute top-[-60px] left-[-60px] text-[hsl(32_98%_52%)] opacity-[0.05]"
        style={{ transform: "rotate(15deg)" }}
      >
        <AshokaWheel size={260} />
      </div>
    </div>
  );
}
